// @ts-nocheck
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { generateClient } from "aws-amplify/api";
import { getCurrentUser } from "aws-amplify/auth";
import { PageContainer, PageEmptyState, PageHeader } from "../components/AssetBreadCrumb";
import FilterBar from "../components/ReusableComponents/FilterBar";
import ModelsToolbar from "../components/Models/ModelsToolbar";
import Select from "../components/ReusableComponents/Select";
import { Skeleton, SkeletonGroup } from "../components/Skeleton";
import DatasetCard, { DatasetCardSkeleton } from "../components/Datasets/DatasetCard";
import ModelSchemaCard, { ModelSchemaCardSkeleton } from "../components/Models/ModelSchemaCard";
import { ReactComponent as PlusIcon } from "../assets/icons/plus.svg";
import { ReactComponent as LayersIcon } from "../assets/icons/layers-icon.svg";
import { ReactComponent as ImportIcon } from "../assets/icons/upload-01-new.svg";
import { ReactComponent as ErodeIcon } from "../assets/icons/erode_icon.svg";
import { usePostHogCapture } from "../hooks/usePostHogCapture";
import { useCreatorMode } from "../hooks/useCreatorMode";
import { useLinkedModal } from "../hooks/useLinkedModal";
import { setModalState, modalViewsConst } from "../store/modalSlice";
import { fetchDatasets, refreshDatasetRecordCount } from "../store/datasetSlice";
import {
  fetchProcessedModelSchemas,
  editProcessedModelSchemaFunc,
} from "../store/processedModelSchemaSlice";
import { fetchModelSchemaTemplates } from "../store/modelSchemaTemplateSlice";
import { ProcessedModelRunHistoryService } from "../services/processedModelRunHistoryService";
import { createRunRequest, listOutputs } from "../services/historicalModelService";
import { describeHistoricalStatus } from "../utils/historicalModelRuns";
import {
  POLLER_INTERVAL_LABEL,
  QUEUED_BUTTON_HOLD_MS,
  setScheduleRowsStatus,
  triggerRunOnce,
} from "../utils/processedModelScheduleSync";
import {
  DEFAULT_MODEL_SORT,
  exportModelSchema,
  fetchModelOutputSchemas,
  filterAndSortModels,
  getAvailableCategories,
  mapWithConcurrency,
  MODEL_SORT_OPTIONS,
  parseSchemaImportFile,
  RUN_LOAD_FAILED,
  toModelSortKey,
} from "../utils/modelsList";
import { POSTHOG_EVENTS } from "../utils/posthogEvents";
import { getUserForCard } from "../graphql/customQueries";
import { getCurrentUserGroups, getCurrentUserOwnerCandidates } from "../utils/ownerScope";
import {
  describeDatasetVolume,
  DATASET_SORT_OPTIONS,
  canManageDataset,
  groupsResolved,
  canUploadToDataset,
  initialDatasetDetailTab,
  sortDatasets
} from "../utils/datasetUtils";
import { closeUrlParams, editUrlParams, modalShowsDetailLink, readDetailLink, setDetailLink } from "../utils/detailLinks";

// The views this page switches between; the count is appended at render
const DATASET_TABS = [
  { id: "all", label: "All" },
  { id: "personal", label: "My Uploads" },
  { id: "subscribed", label: "Organization" },
  { id: "models", label: "Models" },
];

const MODEL_STATUS_TABS = ["all", "active", "inactive"];

// How long the datasets fetch waits for the user's organizations before going ahead without them
const ORGANIZATION_WAIT_MS = 5000;

const DatasetsPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { capture } = usePostHogCapture();
  // New Dataset / New Model show only in creator mode (or for a full Admin); editing stays owner-only either way
  const { canCreate } = useCreatorMode();
  // Open to every user: admins see datasets and models, everyone else datasets only (their own and their
  // organization's, as Dataset @auth scopes them), editing only the ones they own and uploading to those and
  // their organization's. Null until the session is read.
  const [cognitoGroups, setCognitoGroups] = useState(null);
  // Every signed-in user sees the Models tab (stage 4, decision 1 A); it only waits for the session's groups to be read
  const showModels = groupsResolved(cognitoGroups);
  const datasetTabs = showModels ? DATASET_TABS : DATASET_TABS.filter((tab) => tab.id !== "models");
  // ?tab=models is where a deploy from Model Templates lands
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") === "models" ? "models" : "all");
  // Every stored owner format for the current user, so "is this mine" matches however the row was written
  const [ownerCandidates, setOwnerCandidates] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [sortBy, setSortBy] = useState("name");
  // Uploaded datasets, or those a historical model writes (Dataset.sourceModelId): "all" | "uploaded" | "model"
  const [sourceFilter, setSourceFilter] = useState("all");
  const [refreshingId, setRefreshingId] = useState(null);

  // Model filters — only the Models tab shows them; All applies the search and the mapped sort
  const [modelSortBy, setModelSortBy] = useState(DEFAULT_MODEL_SORT);
  const [modelStatusTab, setModelStatusTab] = useState("all");
  const [selectedCategories, setSelectedCategories] = useState([]);
  // Forecast-records list, used only to hand the output modal each model's outputs
  const [outputSchemas, setOutputSchemas] = useState([]);
  // undefined = still loading, null = never ran, RUN_LOAD_FAILED = lookup failed
  const [latestRuns, setLatestRuns] = useState({});
  // Owner User records keyed by user id; null caches a miss so it isn't re-fetched every render
  const [ownersById, setOwnersById] = useState({});
  // HISTORICAL models: the series output's data end, or the datasets the outputs write, for the card's "Data to …" line
  // (null = none read)
  const [outputSummaries, setOutputSummaries] = useState({});
  const summariesInFlight = useRef(new Set());
  const [togglingIds, setTogglingIds] = useState([]);
  const [queuingIds, setQueuingIds] = useState([]);
  const [queuedIds, setQueuedIds] = useState([]);
  // AdminReadOnly sees models but only has [read] on ProcessedModelSchema — seeing is not permission to mutate. Full Admin only.
  const isFullAdmin = Boolean(cognitoGroups?.includes("Admin"));

  // Get data from Redux store
  const { personalDatasets, organizationDatasets, loading } = useSelector(
    (state) => state.datasets
  );
  // state.user.currentOrganization never existed, so every org dataset fell to My Uploads on reload; use the loaded org list
  const organizationsById = useSelector((state) => state.user?.organizations?.data || {});
  const organizationIdsKey = Object.keys(organizationsById).sort().join(",");
  // Datasets are sorted into personal and organization against the user's org ids, so they wait for that list; a stalled org lookup stops blocking them after a few seconds
  const organizationsLoaded = useSelector((state) => state.user?.organizations?.loaded);
  const [organizationWaitExpired, setOrganizationWaitExpired] = useState(false);
  // Set when the user lookup or the datasets fetch fails, so the page leaves its skeleton; bumping the retry key refetches
  const [datasetsLoadError, setDatasetsLoadError] = useState(false);
  const [datasetsRetryKey, setDatasetsRetryKey] = useState(0);
  // The org ids the last successful datasets read used, so a read made before the orgs loaded isn't taken as complete
  const [datasetsFetchedFor, setDatasetsFetchedFor] = useState(null);
  // The models list itself is ready once the shared slice reports a settled list after this visit asked for it
  const [modelOutputsRead, setModelOutputsRead] = useState(false);
  // The wizard stores permissionDetails.id in ProcessedModelSchema.owner, so compare against that
  const currentUserId = useSelector((state) => state.user?.permissionDetails?.id);
  const processedModelSchemas = useSelector((state) => state.processedModelSchemas);
  const schemasLoading = processedModelSchemas?.loading;
  // Templates carry the category metadata; schemas link to them via schemaType.
  const templatesById = useSelector((state) => state.modelSchemaTemplates?.templates || {});
  const templatesLoading = useSelector((state) => state.modelSchemaTemplates?.loading);

  // Hidden file input used by the "Import" button to upload an exported schema JSON
  const importInputRef = useRef(null);
  // Ids currently being fetched — the state guards below read a closure value, so without these two overlapping effect runs would issue the same queries twice
  const runsInFlight = useRef(new Set());
  const ownersInFlight = useRef(new Set());
  // Pending "Queued" re-enable timers, keyed by schema id — cleared on unmount
  const queuedTimers = useRef({});

  useEffect(
    () => () => {
      Object.values(queuedTimers.current).forEach(clearTimeout);
      queuedTimers.current = {};
    },
    []
  );

  useEffect(() => {
    getCurrentUserGroups().then(setCognitoGroups);
    getCurrentUserOwnerCandidates().then(setOwnerCandidates);
  }, []);

  // A non-admin who follows a ?tab=models link lands on All (only once the groups are known, or an admin
  // reloading on the Models tab would be bounced off it)
  useEffect(() => {
    if (cognitoGroups && !showModels && activeTab === "models") setActiveTab("all");
  }, [cognitoGroups, showModels, activeTab]);

  useEffect(() => {
    if (organizationsLoaded) return undefined;
    const timer = setTimeout(() => setOrganizationWaitExpired(true), ORGANIZATION_WAIT_MS);
    return () => clearTimeout(timer);
  }, [organizationsLoaded]);

  // Keyed on the joined ids, not the object: a new organizations reference for the same orgs must not refetch the list
  useEffect(() => {
    if (!organizationsLoaded && !organizationWaitExpired) return;
    const fetchData = async () => {
      setDatasetsLoadError(false);
      try {
        const user = await getCurrentUser();
        const organizationIds = organizationIdsKey ? organizationIdsKey.split(",") : [];
        // unwrap so a rejected fetch lands in the catch; the slice's shared loading flag also flips on failed edits
        await dispatch(fetchDatasets({ userId: user.userId, organizationIds })).unwrap();
        setDatasetsFetchedFor(organizationIdsKey);
      } catch (error) {
        console.error("Error fetching datasets:", error);
        setDatasetsLoadError(true);
      }
    };

    fetchData();
  }, [dispatch, organizationIdsKey, organizationsLoaded, organizationWaitExpired, datasetsRetryKey]);

  // The cards read the shared store, so a toggle or edit shows at once; the forecast-records list only feeds the output modal
  const loadModels = useCallback(async () => {
    dispatch(fetchProcessedModelSchemas());
    dispatch(fetchModelSchemaTemplates());
    try {
      setOutputSchemas(await fetchModelOutputSchemas());
    } catch (error) {
      console.error("Error fetching model outputs:", error);
    }
    setModelOutputsRead(true);
  }, [dispatch]);

  // A forecast uploaded into an external model (UploadForecastModal) is a new issue the cards and the output modal
  // should show; re-read the models' forecasts when the slice says one landed
  const forecastsUploadedAt = useSelector((state) => state.processedModelSchemas?.forecastsUploadedAt || 0);
  const seenForecastsUploadedAt = useRef(forecastsUploadedAt);
  useEffect(() => {
    if (seenForecastsUploadedAt.current === forecastsUploadedAt) return;
    seenForecastsUploadedAt.current = forecastsUploadedAt;
    loadModels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forecastsUploadedAt]);

  // Non-admins never fetch models
  useEffect(() => {
    if (showModels) loadModels();
  }, [loadModels, showModels]);

  useEffect(() => {
    capture(POSTHOG_EVENTS.MODEL_GARDEN_PAGE_VIEWED);
    // Fire once on mount — capture identity is not stable across renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Empty for non-admins, so the counts, the All view and the per-card lookups below leave models out
  const models = useMemo(
    () => (showModels ? Object.values(processedModelSchemas?.schemas || {}) : []),
    [processedModelSchemas?.schemas, showModels]
  );

  // Merge each model's outputs in, so search matches output names and the modal has something to show
  const modelsWithOutputs = useMemo(() => {
    const outputsById = Object.fromEntries(outputSchemas.map((s) => [s.id, s.outputs]));
    return models.map((m) => (outputsById[m.id] ? { ...m, outputs: outputsById[m.id] } : m));
  }, [models, outputSchemas]);

  const handleAddDataset = () => {
    dispatch(
      setModalState({
        modalOpen: true,
        modalView: modalViewsConst.newHistoricalDataset,
        properties: { __modalSize: "lg" }
      })
    );
  };

  const handleUploadData = (dataset) => {
    dispatch(setModalState({
      modalOpen: true,
      modalView: modalViewsConst.uploadDatasetData,
      properties: { __modalSize: "lg", dataset }
    }));
  };

  // The detail view (overview chart, upload log, processing) is its own modal — as a table row the chart shared
  // a cell with a horizontal scroller. A dataset with no data opens on its upload log, where a failed first
  // upload is explained.
  // The click only writes ?dataset=<id>; the link effect below opens the modal, so a shared URL opens it the same way
  const handleOpenDataset = (dataset) => {
    editUrlParams(navigate, (params) => setDetailLink(params, { kind: "dataset", id: dataset.id }), false);
  };

  // Surface the outcome through the app's notification modal — this action used to fail in silence
  const notify = (header, details) => {
    dispatch(
      setModalState({
        modalOpen: true,
        modalView: modalViewsConst.notification,
        properties: { __modalSize: "sm", header, details },
      })
    );
  };

  const handleRefreshRecordCount = async (dataset) => {
    setRefreshingId(dataset.id);
    try {
      const result = await dispatch(refreshDatasetRecordCount({ datasetId: dataset.id }));
      if (refreshDatasetRecordCount.rejected.match(result)) {
        notify("Recheck failed", result.payload || "Could not recheck this dataset's record count.");
        return;
      }
      notify("Record count rechecked", `"${dataset.name}" now reads ${describeDatasetVolume(result.payload)}.`);
    } finally {
      setRefreshingId(null);
    }
  };

  // The shared delete modal, so datasets confirm the way schedules, projects and models do
  // Delete lives in the edit modal's footer, beside Cancel and Save
  const handleEditDataset = (dataset) => {
    dispatch(setModalState({
      modalOpen: true,
      modalView: modalViewsConst.editDataset,
      properties: { __modalSize: "lg", dataset, canDelete: canDeleteDataset(dataset) }
    }));
  };

  // The card's "not mapped" flag: the same edit modal, opened on the column mapping
  const handleMapColumns = (dataset) => {
    dispatch(setModalState({
      modalOpen: true,
      modalView: modalViewsConst.editDataset,
      properties: { __modalSize: "lg", dataset, focusMapping: true, source: "card", canDelete: canDeleteDataset(dataset) }
    }));
  };

  // Latest run per schema so cards can show "Ran 2h ago · failed". One query per card, capped at FETCH_CONCURRENCY in flight so a large grid can't saturate the connection queue. Only ids not already cached are fetched, so toggling one model doesn't re-query the whole grid.
  useEffect(() => {
    const loadLatestRuns = async () => {
      const ids = models
        .map((m) => m.id)
        .filter((id) => !(id in latestRuns) && !runsInFlight.current.has(id));
      if (ids.length === 0) return;
      ids.forEach((id) => runsInFlight.current.add(id));

      try {
        const entries = await mapWithConcurrency(ids, async (id) => {
          try {
            const run = await ProcessedModelRunHistoryService.getLatestRun(id, {
              throwOnError: true,
            });
            return [id, run];
          } catch (err) {
            // Cache the failure — rendering it as "never run" would state something untrue
            console.error(`Failed to load latest run for ${id}:`, err);
            return [id, RUN_LOAD_FAILED];
          }
        });
        setLatestRuns((prev) => ({ ...prev, ...Object.fromEntries(entries) }));
      } finally {
        // Release even on an unexpected throw, or these ids would never be retried
        ids.forEach((id) => runsInFlight.current.delete(id));
      }
    };

    loadLatestRuns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [models]);

  // A historical model's card says how far its data goes: read its outputs once per model
  useEffect(() => {
    const loadSummaries = async () => {
      const ids = models
        .filter((m) => m.modelKind === "HISTORICAL")
        .map((m) => m.id)
        .filter((id) => !(id in outputSummaries) && !summariesInFlight.current.has(id));
      if (ids.length === 0) return;
      ids.forEach((id) => summariesInFlight.current.add(id));
      try {
        const entries = await mapWithConcurrency(ids, async (id) => {
          try {
            const outputs = await listOutputs(id);
            const targetDatasetIds = outputs.filter((o) => o.storageMode === "DATASET" && o.targetDatasetId).map((o) => o.targetDatasetId);
            const writesDataset = targetDatasetIds.length > 0;
            return [id, writesDataset ? { writesDataset, targetDatasetIds } : null];
          } catch (err) {
            console.error(`Failed to load outputs for ${id}:`, err);
            return [id, null];
          }
        });
        setOutputSummaries((prev) => ({ ...prev, ...Object.fromEntries(entries) }));
      } finally {
        ids.forEach((id) => summariesInFlight.current.delete(id));
      }
    };
    loadSummaries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [models]);

  // The card line of a HISTORICAL model (the wording rule: data and runs, never "issued" or a schedule)
  const historicalStatusLine = (model) => {
    if (model.modelKind !== "HISTORICAL") return undefined;
    const run = latestRuns[model.id];
    const status = run === undefined || run === null || run === RUN_LOAD_FAILED ? null : String(run.status || "").toUpperCase();
    const lastRun =
      status === null
        ? null
        : { status: status === "FAILED" ? "failed" : status === "PROCESSING" ? "running" : "succeeded", at: run.updatedAt || run.createdAt };
    const summary = outputSummaries[model.id];
    // A model writing datasets: the newest data end across them, read from the datasets already in the store
    const datasetEnds = (summary?.targetDatasetIds || [])
      .map((id) => (personalDatasets || {})[id] || (organizationDatasets || {})[id])
      .map((dataset) => {
        try {
          const range = typeof dataset?.timeRange === "string" ? JSON.parse(dataset.timeRange) : dataset?.timeRange;
          return range?.endDate || null;
        } catch {
          return null;
        }
      })
      .filter(Boolean)
      .sort();
    return describeHistoricalStatus(
      { dataEnd: datasetEnds[datasetEnds.length - 1] ?? null, writesDataset: !!summary?.writesDataset, lastRun, paused: model.isActive === false },
      new Date().toISOString()
    );
  };

  // "Written by <model>" on a dataset card: the model's name when it is in the list, else just that a model wrote it
  const modelNameById = useMemo(() => Object.fromEntries(models.map((m) => [m.id, m.name])), [models]);

  // Resolve each schema's owner User for the card's menu. Only ids not already cached are fetched, so this settles after one pass rather than refiring as the grid re-renders.
  useEffect(() => {
    const loadOwners = async () => {
      const ids = Array.from(new Set(models.map((m) => m.owner).filter(Boolean))).filter(
        (id) => !(id in ownersById) && !ownersInFlight.current.has(id)
      );
      if (ids.length === 0) return;
      ids.forEach((id) => ownersInFlight.current.add(id));

      try {
        const apiClient = generateClient();
        const entries = await mapWithConcurrency(ids, async (id) => {
          try {
            const { data } = await apiClient.graphql({ query: getUserForCard, variables: { id } });
            const user = data?.getUser;
            return [id, user ? { name: user.name, profilePictureUrl: user.profilePictureUrl } : null];
          } catch (err) {
            console.error(`Failed to load owner ${id}:`, err);
            return [id, null];
          }
        });
        // Cache misses too, so a failed lookup isn't retried on every render
        setOwnersById((prev) => ({ ...prev, ...Object.fromEntries(entries) }));
      } finally {
        // Release even on an unexpected throw, or these ids would never be retried
        ids.forEach((id) => ownersInFlight.current.delete(id));
      }
    };

    loadOwners();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [models]);

  // Mirrors ProcessedModelSchema @auth: Admin has full CRUD, the stored owner has CRUD, and org members match only { allow: groups, groupsField: "organizationId", operations: [read] }. Org-owned schemas store the ORG OWNER's id, so ordinary members correctly fall to read-only.
  // owner is stored in more than one format (sub, username, sub::username): match them all, as the modal and the run function do
  const canManageModel = (model) => isFullAdmin || (!!model.owner && (model.owner === currentUserId || ownerCandidates.includes(model.owner)));

  const handleCategoryToggle = (category) => {
    setSelectedCategories((prev) =>
      prev.includes(category) ? prev.filter((c) => c !== category) : [...prev, category]
    );
  };

  const handleImportModel = () => {
    importInputRef.current?.click();
  };

  // Read an exported ProcessedModelSchema JSON and open the schema wizard pre-filled to create a NEW schema from it (a fresh id is generated on submit, so no collision).
  const handleImportFileSelected = async (e) => {
    const file = e.target.files?.[0];
    // Reset so picking the same file again re-fires onChange
    e.target.value = "";
    if (!file) return;

    let parsed;
    try {
      parsed = await parseSchemaImportFile(file);
    } catch (err) {
      notify("Import failed", err?.message || "Could not read schema file.");
      return;
    }

    dispatch(
      setModalState({
        modalOpen: true,
        modalView: modalViewsConst.newProcessedModelSchema,
        properties: { __modalSize: "md", importedSchema: parsed },
      })
    );
  };

  // Clicking a model card opens its outputs, the way a dataset card opens its chart. A HISTORICAL model opens its
  // own detail (series, runs, uploads, processing; docs/plans/HISTORICAL_PROCESSED_MODELS_PLAN.md, stage 4)
  // The click only writes ?model=<id>; the link effect below opens the modal, so a shared URL opens it the same way
  const handleOpenModel = (model) => {
    editUrlParams(navigate, (params) => setDetailLink(params, { kind: "model", id: model.id }), false);
  };

  // Shared links: ?dataset=<id> / ?model=<id> open that modal on &view's tab; useLinkedModal keeps link and modal in step
  const detailLink = readDetailLink(searchParams);
  // The dispatch above sets listLoading to pending (or finds one pending), so "success" after it is this visit's list
  const modelsReady = modelOutputsRead && processedModelSchemas?.listLoading === "success";
  const linkedDataset = detailLink?.kind === "dataset" ? (personalDatasets || {})[detailLink.id] || (organizationDatasets || {})[detailLink.id] || null : null;
  // Before the org list loads only a found dataset opens; "not found" waits for the orgs, or for the same timeout the fetch falls back on
  const datasetsReady =
    datasetsFetchedFor === organizationIdsKey && (organizationsLoaded || organizationWaitExpired || Boolean(linkedDataset));
  useLinkedModal({
    linkKey: detailLink ? `${detailLink.kind}:${detailLink.id}` : null,
    ready: detailLink?.kind === "dataset" ? datasetsReady : modelsReady,
    find: () => (detailLink?.kind === "dataset" ? linkedDataset : modelsWithOutputs.find((m) => m.id === detailLink?.id) ?? null),
    open: (found) => {
      const view = searchParams.get("view") || undefined;
      if (detailLink?.kind === "dataset") {
        dispatch(setModalState({
          modalOpen: true,
          modalView: modalViewsConst.datasetGraph,
          properties: { __modalSize: "xl", dataset: found, initialTab: view ?? initialDatasetDetailTab(found) },
        }));
      } else if (found.modelKind === "HISTORICAL") {
        dispatch(setModalState({
          modalOpen: true,
          modalView: modalViewsConst.historicalModel,
          properties: { __modalSize: "xl", model: found, initialTab: view },
        }));
      } else {
        dispatch(setModalState({
          modalOpen: true,
          modalView: modalViewsConst.modelOutputDetail,
          properties: { __modalSize: "xl", schema: found, initialTab: view },
        }));
      }
    },
    isShowing: (modal, key) => {
      const at = key.indexOf(":");
      return modalShowsDetailLink(modal, { kind: key.slice(0, at), id: key.slice(at + 1) });
    },
    clearLink: () => closeUrlParams(navigate, (params) => setDetailLink(params, null), "all"),
    notFound: {
      header: `${detailLink?.kind === "dataset" ? "Dataset" : "Model"} not found`,
      details: "This link points to something that was deleted or that you don't have access to.",
    },
  });

  // Delete lives in the edit wizard's footer, beside Cancel and Save
  const handleEditModel = (model) => {
    dispatch(
      setModalState({
        modalOpen: true,
        modalView: modalViewsConst.newProcessedModelSchema,
        properties: { __modalSize: "md", schemaToEdit: model, canDelete: canManageModel(model) },
      })
    );
  };

  const handleExportModel = async (model) => {
    try {
      await exportModelSchema(model);
      capture(POSTHOG_EVENTS.MODEL_SCHEMA_EXPORTED, { schema_type: model.schemaType });
    } catch (error) {
      console.error("Error exporting model schema:", error);
      notify("Export failed", error?.message || "Failed to export model schema. Please try again.");
    }
  };

  // Flip the schema AND its schedule rows. Row status is what the poller reads — updating only the schema would show "Inactive" while the model kept running on schedule.
  const handleToggleActive = async (model) => {
    if (togglingIds.includes(model.id)) return;
    const nextActive = !model.isActive;
    setTogglingIds((prev) => [...prev, model.id]);
    try {
      // Schema first — if this throws, the schedule rows are untouched and nothing has drifted
      await dispatch(
        editProcessedModelSchemaFunc({
          input: {
            id: model.id,
            isActive: nextActive,
            activeStatus: nextActive ? "ACTIVE" : "INACTIVE",
          },
        })
      ).unwrap();
      capture(POSTHOG_EVENTS.MODEL_ACTIVE_TOGGLED, {
        is_active: nextActive,
        schema_type: model.schemaType,
      });

      // setScheduleRowsStatus resolves rather than throws, so only one message can fire here
      const { failed } = await setScheduleRowsStatus(model.id, nextActive);
      if (failed === -1) {
        notify(
          "Schedules not verified",
          "The model status changed, but its schedules could not be listed. Reload and check the schedule still matches the status shown."
        );
      } else if (failed > 0) {
        notify(
          "Schedules not fully updated",
          "The model status changed but some of its schedules could not be updated. Reload and try again — until then the schedule may not match the status shown."
        );
      }
    } catch (err) {
      console.error("Failed to toggle model active state:", err);
      notify("Update failed", "Could not change the model status. Please try again.");
    } finally {
      setTogglingIds((prev) => prev.filter((id) => id !== model.id));
    }
  };

  // An external template has no processor: the model's forecasts are files its owner (or org) uploads
  const isExternalModel = (model) => templatesById[model?.schemaType]?.external === true;

  // Upload a forecast into an external model: the upload modal (lg) takes the page's modal slot
  const handleUploadForecast = (model) => {
    dispatch(setModalState({
      modalOpen: true,
      modalView: modalViewsConst.uploadForecast,
      properties: { __modalSize: "lg", schema: model },
    }));
  };

  // Queue a one-off run. The poller picks ONCE rows up on its next tick (~5 min), so this is queued rather than immediate — the copy says so rather than implying live execution.
  const handleRunOnce = async (model) => {
    // Nothing to run for an external model; the card's button already reads Upload forecast, this guards any other caller
    if (isExternalModel(model)) {
      handleUploadForecast(model);
      return;
    }
    if (queuingIds.includes(model.id) || queuedIds.includes(model.id)) return;
    setQueuingIds((prev) => [...prev, model.id]);
    try {
      // A historical model's Run now is a run request the run function picks up within seconds (stage 2)
      if (model.modelKind === "HISTORICAL") {
        await createRunRequest({ processedModelSchemaId: model.id, triggerType: "MANUAL" });
        setQueuedIds((prev) => [...prev, model.id]);
        queuedTimers.current[model.id] = setTimeout(() => {
          delete queuedTimers.current[model.id];
          setQueuedIds((prev) => prev.filter((id) => id !== model.id));
        }, 15_000);
        capture(POSTHOG_EVENTS.HISTORICAL_MODEL_RUN_REQUESTED, { model_id: model.id, trigger: "MANUAL", source: "card" });
        notify("Run queued", `"${model.name}" starts within seconds. Open the model to watch it.`);
        return;
      }
      await triggerRunOnce(model);
      setQueuedIds((prev) => [...prev, model.id]);
      // Re-enable after a short hold, so the button doesn't read "Queued" forever on a long-lived session. triggerRunOnce still dedupes server-side against a pending row.
      queuedTimers.current[model.id] = setTimeout(() => {
        delete queuedTimers.current[model.id];
        setQueuedIds((prev) => prev.filter((id) => id !== model.id));
      }, QUEUED_BUTTON_HOLD_MS);
      capture(POSTHOG_EVENTS.MODEL_RUN_QUEUED, { schema_type: model.schemaType });
      notify(
        "Run queued",
        `"${model.name}" is queued and will start within ${POLLER_INTERVAL_LABEL}. Its status will appear in the run history once it completes.`
      );
    } catch (err) {
      console.error("Failed to queue model run:", err);
      notify("Could not queue run", "Failed to queue a run for this model. Please try again.");
    } finally {
      setQueuingIds((prev) => prev.filter((id) => id !== model.id));
    }
  };

  const personalDatasetsArray = Object.values(personalDatasets || {});
  const orgDatasetsArray = Object.values(organizationDatasets || {});
  const currentDatasets =
    activeTab === "personal"
      ? personalDatasetsArray
      : activeTab === "all"
      ? [...personalDatasetsArray, ...orgDatasetsArray]
      : orgDatasetsArray;

  const matchesSource = (dataset) =>
    sourceFilter === "all" || (sourceFilter === "model" ? Boolean(dataset.sourceModelId) : !dataset.sourceModelId);
  const filterDatasets = (list) =>
    sortDatasets(
      list.filter(dataset =>
        matchesSource(dataset) &&
        (dataset.name.toLowerCase().includes(searchValue.toLowerCase()) ||
        dataset.description?.toLowerCase().includes(searchValue.toLowerCase()))
      ),
      sortBy
    );
  const filteredDatasets = filterDatasets(currentDatasets);
  // All splits its datasets into a personal and an organization section
  const filteredPersonalDatasets = filterDatasets(personalDatasetsArray);
  const filteredOrgDatasets = filterDatasets(orgDatasetsArray);

  // Deleting is owner-only; on All, only the rows that are yours qualify
  const canDeleteDataset = (dataset) =>
    (activeTab === "personal" || activeTab === "all") &&
    Boolean((personalDatasets || {})[dataset.id]);

  const isModelsTab = activeTab === "models";
  const availableCategories = useMemo(
    () => getAvailableCategories(models, templatesById),
    [models, templatesById]
  );
  // Chips and the model sort belong to the Models tab; All reuses its dataset sort, mapped onto the nearest model key
  const filteredModels = filterAndSortModels({
    models: modelsWithOutputs,
    templatesById,
    search: searchValue,
    categories: isModelsTab ? selectedCategories : [],
    sortBy: isModelsTab ? modelSortBy : toModelSortKey(sortBy),
    latestRuns,
  });
  const activeModels = filteredModels.filter((model) => model.isActive);
  const inactiveModels = filteredModels.filter((model) => !model.isActive);
  const statusModels = !isModelsTab || modelStatusTab === "all"
    ? filteredModels
    : modelStatusTab === "active"
    ? activeModels
    : inactiveModels;
  const modelStatusCountFor = (tab) =>
    tab === "all"
      ? models.length
      : models.filter((model) => (tab === "active" ? model.isActive : !model.isActive)).length;
  // Counts ride in the option labels now that status is a Select in the search card
  const modelStatusOptions = MODEL_STATUS_TABS.map((id) => ({
    value: id,
    label: `${id.charAt(0).toUpperCase() + id.slice(1)} (${modelStatusCountFor(id)})`,
  }));

  const modelsLoading =
    ((schemasLoading === "pending" || schemasLoading === "idle") && models.length === 0) ||
    ((templatesLoading === "pending" || templatesLoading === "idle") && Object.keys(templatesById).length === 0);
  const hasModels = models.length > 0;
  // Keyed off the schemas fetch only — templates are supplementary, and a failed edit/delete also lands on "failed", so require an empty grid before claiming the page couldn't load.
  const modelsLoadError = !modelsLoading && !hasModels && schemasLoading === "failed";

  // Home's eyebrow count: the tab's total, narrowing to "x of y" while a filter is on
  const headerCount =
    isModelsTab
      ? models.length
      : activeTab === "all"
      ? currentDatasets.length + models.length
      : currentDatasets.length;
  const shownCount =
    isModelsTab
      ? statusModels.length
      : activeTab === "all"
      ? filteredDatasets.length + filteredModels.length
      : filteredDatasets.length;

  const goToTemplates = () => navigate("/home/model-templates");

  // iconOnlyOnMobile collapses the button to a square icon under sm — header only. The empty state keeps its label, since a lone icon there reads as nothing.
  const importButton = (label, iconOnlyOnMobile = false) => (
    <button
      onClick={handleImportModel}
      className={`eai-btn-outline ${iconOnlyOnMobile ? "eai-btn-icon-mobile" : ""}`}
      type="button"
      aria-label={label}
      title="Create a new model from an exported schema JSON"
    >
      <span className="h-4 w-4 flex items-center justify-center shrink-0">
        <ImportIcon className="h-4 w-4" style={{ "--stroke-0": "currentColor" }} />
      </span>
      <span className={iconOnlyOnMobile ? "hidden sm:inline" : undefined}>{label}</span>
    </button>
  );

  // One renderer for the All section, the grouped Models view and the single-status slices
  const renderModelGrid = (list) => (
    <div className="eai-card-grid">
      {list.map((model) => (
        <ModelSchemaCard
          key={model.id}
          model={model}
          templatesById={templatesById}
          owner={model.owner ? ownersById[model.owner] : null}
          latestRun={latestRuns[model.id]}
          canManage={canManageModel(model)}
          isToggling={togglingIds.includes(model.id)}
          isQueuing={queuingIds.includes(model.id)}
          hasQueuedRun={queuedIds.includes(model.id)}
          onToggleActive={handleToggleActive}
          onRunOnce={handleRunOnce}
          onUploadForecast={handleUploadForecast}
          onEdit={handleEditModel}
          onExport={handleExportModel}
          onOpen={handleOpenModel}
          onPostSampleError={(message) => notify("Sample POST failed", message)}
          statusLine={historicalStatusLine(model)}
        />
      ))}
    </div>
  );

  // One renderer for each dataset scope; scope picks the empty-state copy
  const renderDatasets = (list, scope) =>
    datasetsLoadError ? (
      <PageEmptyState
        title="Couldn't load your datasets"
        description="Something went wrong fetching your datasets. Check your connection and try again."
        action={
          <button onClick={() => setDatasetsRetryKey((k) => k + 1)} className="eai-btn-cta" type="button">
            Try again
          </button>
        }
      />
    ) : list.length === 0 ? (
      <PageEmptyState
        icon={<LayersIcon className="h-6 w-6 [&_path]:stroke-current" />}
        description={
          searchValue
            ? `No datasets found matching "${searchValue}"`
            : scope === "subscribed"
              ? "No organization datasets yet. Datasets your organization publishes appear here."
              : canCreate
                ? "You haven't created any datasets yet. Upload a CSV to get started."
                : "No datasets yet."
        }
        action={
          !searchValue &&
          scope !== "subscribed" &&
          canCreate && (
            <button onClick={handleAddDataset} className="eai-btn-cta" type="button">
              <PlusIcon className="h-4 w-4" />
              Create Your First Dataset
            </button>
          )
        }
      />
    ) : (
      <div className="eai-card-grid">
        {list.map((dataset) => (
          <DatasetCard
            key={dataset.id}
            dataset={dataset}
            canManage={canManageDataset(dataset, cognitoGroups, ownerCandidates)}
            canUpload={canUploadToDataset(dataset, cognitoGroups, ownerCandidates)}
            onOpen={handleOpenDataset}
            onEdit={handleEditDataset}
            onMapColumns={handleMapColumns}
            onUpload={handleUploadData}
            onRefreshCount={handleRefreshRecordCount}
            isRefreshing={refreshingId === dataset.id}
            sourceModelName={dataset.sourceModelId ? modelNameById[dataset.sourceModelId] || "a model" : null}
          />
        ))}
      </div>
    );

  const renderModels = () => {
    if (modelsLoading) {
      return (
        <SkeletonGroup count={6} label="Loading models" className="eai-card-grid">
          <ModelSchemaCardSkeleton />
        </SkeletonGroup>
      );
    }
    if (modelsLoadError) {
      return (
        <PageEmptyState
          title="Couldn't load your models"
          description="Something went wrong fetching your models. Check your connection and try again."
          action={
            <button onClick={loadModels} className="eai-btn-cta" type="button">
              Try again
            </button>
          }
        />
      );
    }
    if (!hasModels) {
      return (
        <PageEmptyState
          icon={<ErodeIcon className="h-6 w-6" />}
          title="No models yet"
          description={
            canCreate
              ? "Deploy a published template — wind, solar, hydro, load — against your own assets, or import a schema JSON."
              : "Models shared with you appear here."
          }
          action={
            canCreate && (
              <>
                <button onClick={goToTemplates} className="eai-btn-cta" type="button">
                  Browse Model Templates
                </button>
                {isFullAdmin && importButton("Import JSON")}
              </>
            )
          }
        />
      );
    }
    if (statusModels.length === 0) {
      return (
        <PageEmptyState
          title="No models match your filters"
          description={
            searchValue
              ? `No models found matching "${searchValue}"`
              : "Try a different status or clear the category filters."
          }
        />
      );
    }
    // The Models tab's All view groups under Active/Inactive headers, like Custom Views
    if (isModelsTab && modelStatusTab === "all") {
      return (
        <>
          {activeModels.length > 0 && (
            <>
              <h2 className="eai-section-label">Active</h2>
              {renderModelGrid(activeModels)}
            </>
          )}
          {inactiveModels.length > 0 && (
            <>
              <h2 className={`eai-section-label ${activeModels.length > 0 ? "eai-gap-section" : ""}`}>
                Inactive
              </h2>
              {renderModelGrid(inactiveModels)}
            </>
          )}
        </>
      );
    }
    return renderModelGrid(statusModels);
  };

  // Also held while the groups are read, so nobody sees the other role's tabs flash first
  // "idle" too: the fetch waits on the org list, and until it fires the page would flash its empty states
  if (loading === "pending" || (loading === "idle" && !datasetsLoadError) || cognitoGroups === null) {
    // Header, filter card and a card grid silhouette, so the page doesn't jump when data lands
    return (
      <PageContainer>
        <div role="status" aria-label="Loading datasets">
          <div className="eai-gap-header">
            <Skeleton className="h-3 w-16" />
            <Skeleton className="mt-2 h-7 w-48" radius="md" />
          </div>
          <Skeleton className="eai-gap-toolbar h-20 w-full" radius="md" />
          <div className="eai-card-grid">
            {Array.from({ length: 6 }).map((_, i) => (
              <DatasetCardSkeleton key={i} />
            ))}
          </div>
        </div>
      </PageContainer>
    );
  }

  // A Select, not the seg — four labels overflow a phone-width panel
  const scopeSelect = (
    <Select
      id="datasets-scope"
      label="Scope"
      value={activeTab}
      onChange={(e) => setActiveTab(e.target.value)}
      options={datasetTabs.map(({ id, label }) => ({ value: id, label }))}
    />
  );

  return (
    <PageContainer>
      <input
        ref={importInputRef}
        type="file"
        accept="application/json,.json"
        className="hidden"
        onChange={handleImportFileSelected}
      />
      <PageHeader
        eyebrow={
          <>
            Data
            <span className="eai-count-pill ml-2">
              {shownCount === headerCount ? headerCount : `${shownCount} of ${headerCount}`}
            </span>
          </>
        }
        title={showModels ? "My Data & Models" : "My Data"}
        action={
          isModelsTab ? (
            <>
              {isFullAdmin && importButton("Import", true)}
              {/* New models start from a template, so this goes to Model Templates rather than opening a blank form */}
              {canCreate && (
                <button
                  onClick={goToTemplates}
                  className="eai-btn-cta eai-btn-icon-mobile"
                  type="button"
                  aria-label="New Model"
                  title="New Model"
                >
                  <PlusIcon className="h-4 w-4 shrink-0" />
                  <span className="hidden sm:inline">New Model</span>
                </button>
              )}
            </>
          ) : canCreate && (activeTab !== "all" || !showModels) ? (
            /* Label folds away under sm like the other list pages, so the pill needs its own name there */
            <button
              onClick={handleAddDataset}
              className="eai-btn-cta eai-btn-icon-mobile"
              type="button"
              aria-label="New Dataset"
              title="New Dataset"
            >
              <PlusIcon className="h-4 w-4 shrink-0" />
              <span className="hidden sm:inline">New Dataset</span>
            </button>
          ) : undefined
        }
      />

      {/* Desktop only — on a phone the scope moves into the Filters panel as a Select so the page opens like Home */}
      <div className="mb-5 hidden items-center gap-3 sm:flex">
        <div className="eai-seg max-w-full overflow-x-auto" role="group" aria-label="Dataset scope">
          {datasetTabs.map(({ id, label }) => {
            const active = activeTab === id;
            const count =
              id === "personal"
                ? personalDatasetsArray.length
                : id === "models"
                ? models.length
                : id === "subscribed"
                ? orgDatasetsArray.length
                : personalDatasetsArray.length + models.length + orgDatasetsArray.length;
            return (
              <button
                key={id}
                type="button"
                onClick={() => setActiveTab(id)}
                className={`eai-seg-item whitespace-nowrap px-2.5 sm:px-3.5 ${active ? "eai-seg-item-active" : ""}`}
                aria-current={active ? "true" : undefined}
              >
                {label}
                <span className="eai-count-pill hidden sm:inline-flex">{count}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* One search shared across the tabs; the Models tab swaps in the toolbar that carries category chips and the model sort */}
      {isModelsTab ? (
        (modelsLoading || hasModels) && !modelsLoadError && (
          <ModelsToolbar
            sticky
            className="eai-gap-toolbar"
            searchId="models-search"
            searchValue={searchValue}
            onSearchChange={setSearchValue}
            searchPlaceholder="Search by model, output, or description"
            availableChips={modelsLoading ? [] : availableCategories}
            selectedChips={selectedCategories}
            onChipToggle={handleCategoryToggle}
            countLabel={modelsLoading ? "" : `${statusModels.length} of ${models.length} models`}
            sortBy={modelSortBy}
            onSortChange={setModelSortBy}
            sortOptions={MODEL_SORT_OPTIONS}
            sortAriaLabel="Sort models"
            statusValue={modelStatusTab}
            onStatusChange={setModelStatusTab}
            statusOptions={modelStatusOptions}
            disabled={modelsLoading}
            mobileControls={scopeSelect}
            mobileActiveCount={1}
          />
        )
      ) : (
        <FilterBar
          sticky
          searchId="datasets-search"
          searchValue={searchValue}
          onSearchChange={setSearchValue}
          searchPlaceholder={
            activeTab === "all" && showModels
              ? "Search by name, parameter, output, or description"
              : "Search by name, parameter, or description"
          }
          // Uploaded datasets or model-written ones, between search and sort; FilterBar's "" is All, the page's state keeps "all"
          filters={[
            {
              id: "datasets-source",
              label: "Source",
              value: sourceFilter === "all" ? "" : sourceFilter,
              onChange: (value) => setSourceFilter(value || "all"),
              options: [
                { value: "uploaded", label: "Uploaded" },
                { value: "model", label: "Written by a model" },
              ],
              allLabel: "All sources",
            },
          ]}
          sortBy={sortBy}
          onSortChange={setSortBy}
          sortOptions={DATASET_SORT_OPTIONS}
          onClear={() => {
            setSearchValue("");
            setSourceFilter("all");
          }}
          isFiltered={Boolean(searchValue) || sourceFilter !== "all"}
          // Scope only folds into the panel below sm, so only there does Clear reset it
          onClearMobile={() => setActiveTab("all")}
          mobileControls={scopeSelect}
          mobileActiveCount={activeTab === "all" ? 0 : 1}
        />
      )}

      <div>
        {isModelsTab ? (
          renderModels()
        ) : activeTab === "all" ? (
          // All stacks My Uploads, Organization, then Models (admins only) — titles mirror the tabs; each keeps its own empty state
          <>
            <h2 className="eai-section-label">Datasets (My Uploads)</h2>
            {renderDatasets(filteredPersonalDatasets, "personal")}
            <h2 className="eai-section-label eai-gap-section">Datasets (Organization)</h2>
            {renderDatasets(filteredOrgDatasets, "subscribed")}
            {showModels && (
              <>
                <h2 className="eai-section-label eai-gap-section">Models</h2>
                {renderModels()}
              </>
            )}
          </>
        ) : (
          renderDatasets(filteredDatasets, activeTab)
        )}
      </div>
    </PageContainer>
  );
};

export default DatasetsPage;
